import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { DEFAULT_LANG, detectLang, persistLang, translate, ensureLocaleLoaded, getLocaleList } from '@/i18n';

/**
 * React wrapper around the i18n engine.
 *
 * Design decision (Apr 2026): the runtime DOM-level auto-translator has
 * been intentionally DISABLED. Fighting React's reconciliation with a
 * MutationObserver + LLM round-trips is architecturally unreliable — it
 * causes visible flicker, race conditions during hydration, and occasional
 * text corruption. Every professional SaaS does it the same way: every
 * user-facing string is wrapped in `t('key')` and translations live in
 * versioned JSON files (/src/i18n/locales/*.json). Any hardcoded string
 * stays in the developer's source language (DE) until migrated to `t()`.
 *
 * Public API (unchanged):
 *   const { t, lang, switchLang, locales, isSwitching } = useLanguage();
 */

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => detectLang() || DEFAULT_LANG);
  const [isSwitching, setIsSwitching] = useState(false);
  // Bump forces re-render of consumers after async dict load completes.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    try { document.documentElement.lang = lang; } catch { /* ignore */ }
  }, [lang]);

  // Warm the current locale on mount (no-op if bundled).
  useEffect(() => {
    let alive = true;
    ensureLocaleLoaded(lang).then((ok) => {
      if (alive && ok) setTick((x) => x + 1);
    });
    return () => { alive = false; };
  }, [lang]);

  const switchLang = useCallback(async (code) => {
    if (!code || code === lang) return;
    setIsSwitching(true);
    try {
      // Load the target locale dict BEFORE flipping `lang` so consumers
      // never render with an empty dict (which would flash EN/DE fallbacks).
      await ensureLocaleLoaded(code);
      setLang(code);
      persistLang(code);
      setTick((x) => x + 1);
    } finally {
      setIsSwitching(false);
    }
  }, [lang]);

  const t = useCallback(
    (key, vars) => translate(lang, key, vars),
    // tick is intentionally included so consumers re-render when a lazy
    // locale finishes loading mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, tick]
  );

  const value = useMemo(
    () => ({
      t,
      lang,
      switchLang,
      setLang: switchLang,
      isSwitching,
      locales: getLocaleList(),
    }),
    [t, lang, switchLang, isSwitching]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be inside LanguageProvider');
  return ctx;
}
